import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import IconPageLink from '@atoms/IconPageLink/IconPageLink';
import { IAccountDropdown } from '@models/IAccountDropdown';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, SetStateAction } from 'react';

interface Props extends IAccountDropdown {
  readonly accountLabel: string;
  readonly callback: () => void;
  readonly isOpen: boolean;
  readonly setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const dropdownVariants = {
  enter: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: 0.1,
      ease: 'easeInOut',
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      duration: 0.1,
      ease: 'easeInOut',
    },
  },
};

const AccountDropdown = ({
  accountIcon,
  accountLabel,
  callback,
  isOpen,
  loggedInSubtitle,
  loggedInTitle,
  loggedOutTitle,
  loginCta,
  loginEndpoint,
  navigationDropdown,
  setIsOpen,
  signOutCta,
  signOutEndpoint,
  userAuthenticated,
}: Props) => (
  <motion.div
    className="relative max-w-max"
    data-testid="CT-97-Account-Dropdown"
  >
    <button
      className="flex"
      onClick={() => {
        setIsOpen(!isOpen);
        callback();
      }}
      aria-label={accountLabel}
      data-testid="CT-97-Account-Dropdown-Account-Icon"
    >
      <Icon
        name={accountIcon || 'account'}
        className="transition-colors hover:text-yellow"
        colorClassName={classNames(isOpen ? 'text-yellow' : 'text-white')}
      />
    </button>

    <AnimatePresence>
      {isOpen && (
        <div
          onBlur={(e) => {
            const { currentTarget } = e;
            requestAnimationFrame(() => {
              if (!currentTarget.contains(document.activeElement)) {
                setIsOpen(!isOpen);
              }
            });
          }}
          className="lg:-translate-x-3/4 absolute top-10 z-80 flex max-h-[75vh] w-[342px] flex-col items-start gap-10 overflow-y-auto rounded-[10px] bg-white p-10 pb-15"
          data-testid="CT-97-Account-Dropdown-Wrapper"
        >
          {userAuthenticated ? (
            <div className="flex flex-col">
              {loggedInTitle && (
                <div
                  className="font-normal text-md leading-26 lg:text-lg lg:leading-31"
                  data-testid="CT-97-Account-Dropdown-Account-Logged-In-Title"
                >
                  {loggedInTitle}
                </div>
              )}
              {loggedInSubtitle && (
                <div
                  className="body-four"
                  data-testid="CT-97-Account-Dropdown-Account-Logged-In-Subtitle"
                >
                  {loggedInSubtitle}
                </div>
              )}
            </div>
          ) : (
            <>
              {loggedOutTitle && (
                <div
                  className="body-one"
                  data-testid="CT-97-Account-Dropdown-Account-Logged-Out-Title"
                >
                  {loggedOutTitle}
                </div>
              )}
              {loginCta && loginEndpoint && (
                <Button
                  link={{ name: loginCta, url: loginEndpoint }}
                  className="text-center"
                  dataTestId="CT-97-Account-Dropdown-Account-Login-CTA"
                  linkCallback={() => {
                    updateDataLayer({
                      event: 'menuUsed',
                      content_type: 'quick_menu',
                      main_category: 'account',
                      sub_category: loginCta,
                    });
                  }}
                />
              )}
            </>
          )}
          <motion.ul className="flex flex-col items-start gap-13">
            {navigationDropdown?.map((item, index) => (
              <motion.li
                initial="exit"
                animate="enter"
                exit="exit"
                variants={dropdownVariants}
                className="body-two w-full cursor-pointer"
                key={`account-dropdown-list-item-${item.label}`}
                onClick={() => {
                  updateDataLayer({
                    event: 'menuUsed',
                    content_type: 'quick_menu',
                    main_category: 'account',
                    sub_category: item.label,
                  });
                }}
              >
                <IconPageLink
                  {...item}
                  active={false}
                  dataTestId={`CT-97-Account-Dropdown-Account-Icon-Page-Link-${index}`}
                />
              </motion.li>
            ))}
            {signOutCta && signOutEndpoint && userAuthenticated && (
              <li>
                <Button
                  secondary
                  isLightMode
                  link={{ name: signOutCta, url: signOutEndpoint }}
                  dataTestId="CT-97-Account-Dropdown-Account-Logout-CTA"
                />
              </li>
            )}
          </motion.ul>
        </div>
      )}
    </AnimatePresence>
  </motion.div>
);

export default AccountDropdown;
